import { noop } from 'lodash';
import React, { memo } from 'react';
import { useIntl } from 'react-intl';

import { CardSelect } from '~/eds/src/components/CardSelect/CardSelect';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { Image } from '~/eds/src/components/Image/Image';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Select } from '~/eds/src/components/Select/Select';

import { DataFieldType } from '../../../enums/DataFieldType';
import { FieldConfig } from './types';
import { testEnableSort } from './utils';

interface Props {
  config: FieldConfig;
  fieldType: DataFieldType;
  onChange: (updatedConfig: FieldConfig) => void;
}

export const FieldConfiguration = memo(
  ({ config, fieldType, onChange }: Props) => {
    const intl = useIntl();
    const { enableMultiple, optionsConfig, optionValues } = config;
    switch (fieldType) {
      case DataFieldType.ARRAY_MULTIPLE: {
        return (
          <Layout preset="form-fields">
            <FormField<boolean, false>
              label={intl.formatMessage({
                id: 'FieldConfiguration.fieldSettings',
                defaultMessage: 'Field Settings',
                description: 'Label for field settings section',
              })}
              name="field settings"
              input={CardSelect}
              inputProps={{
                options: [
                  {
                    content: (
                      <Image
                        alt={intl.formatMessage({
                          id: 'FieldConfiguration.singleSelectPreviewAlt',
                          defaultMessage: 'single select preview',
                          description:
                            'Alt text for single select preview image',
                        })}
                        image="preview-field-single-select"
                      />
                    ),
                    description: intl.formatMessage({
                      id: 'FieldConfiguration.singleSelectDescription',
                      defaultMessage:
                        'Use this when only a single value should be selected.',
                      description: 'Description for single select option',
                    }),
                    label: intl.formatMessage({
                      id: 'FieldConfiguration.singleSelect',
                      defaultMessage: 'Single Select',
                      description: 'Label for single select option',
                    }),
                    value: false,
                  },
                  {
                    content: (
                      <Image
                        alt={intl.formatMessage({
                          id: 'FieldConfiguration.multipleSelectPreviewAlt',
                          defaultMessage: 'multiple select preview',
                          description:
                            'Alt text for multiple select preview image',
                        })}
                        image="preview-field-multiple-select"
                      />
                    ),
                    description: intl.formatMessage({
                      id: 'FieldConfiguration.multipleSelectDescription',
                      defaultMessage:
                        'Use this when one or more values can be selected.',
                      description: 'Description for multiple select option',
                    }),
                    label: intl.formatMessage({
                      id: 'FieldConfiguration.multipleSelect',
                      defaultMessage: 'Multiple Select',
                      description: 'Label for multiple select option',
                    }),
                    value: true,
                  },
                ],
              }}
              value={enableMultiple}
              onChange={(updatedEnableMultiple) =>
                onChange({
                  ...config,
                  enableMultiple: updatedEnableMultiple ?? false,
                })
              }
            />
            <FormField<string, true>
              description={intl.formatMessage({
                id: 'FieldConfiguration.aiGeneratedOptionsDescription',
                defaultMessage:
                  'AI generated options will be added if field is added to a field model.',
                description:
                  'Description explaining AI generated options behavior',
              })}
              label={intl.formatMessage({
                id: 'FieldConfiguration.fieldOptions',
                defaultMessage: 'Field Option(s)',
                description: 'Label for field options input',
              })}
              name="field-options"
              input={Select}
              inputProps={{
                enableSelectAll: false,
                enableSorting: testEnableSort(optionsConfig),
                isClearable: false,
                isMulti: true,
                noOptionsMessage: intl.formatMessage({
                  id: 'FieldConfiguration.noOptionsMessage',
                  defaultMessage: 'Type to create a new option',
                  description: 'Message shown when no options are available',
                }),
                options: optionValues.map((value) => ({
                  label: value,
                  value,
                })),
                onAddOption: noop,
              }}
              placeholder={intl.formatMessage({
                id: 'FieldConfiguration.optionsPlaceholder',
                defaultMessage: 'Create or select an option',
                description: 'Placeholder text for options input',
              })}
              value={optionValues}
              onChange={(updatedOptionValues) =>
                onChange({
                  ...config,
                  optionValues: updatedOptionValues ?? [],
                })
              }
            />
            <CardSelect<'enable_add' | 'enable_sort', true>
              isMulti={true}
              name="configure field options"
              options={[
                {
                  label: intl.formatMessage({
                    id: 'FieldConfiguration.allowUsersAddOptions',
                    defaultMessage: 'Allow users to add their own options',
                    description:
                      'Label for option to allow users to add custom options',
                  }),
                  description: intl.formatMessage({
                    id: 'FieldConfiguration.allowUsersAddOptionsDescription',
                    defaultMessage:
                      'Any user in the platform can create values for this field.',
                    description:
                      'Description for allowing users to add custom options',
                  }),
                  value: 'enable_add',
                },
                {
                  label: intl.formatMessage({
                    id: 'FieldConfiguration.sortOptionsAlphabetically',
                    defaultMessage: 'Sort options alphabetically',
                    description:
                      'Label for option to sort options alphabetically',
                  }),
                  description: intl.formatMessage({
                    id: 'FieldConfiguration.sortOptionsDescription',
                    defaultMessage:
                      'Enable to sort options alphabetically, otherwise keep the original options order.',
                    description: 'Description for alphabetical sorting option',
                  }),
                  value: 'enable_sort',
                },
              ]}
              value={optionsConfig}
              onChange={(updatedOptionsConfig) =>
                onChange({
                  ...config,
                  optionsConfig: updatedOptionsConfig ?? [],
                })
              }
            />
          </Layout>
        );
      }
      default:
        return null;
    }
  },
);
